import { startRegistration, startAuthentication } from '@simplewebauthn/browser'
import type { RecordModel } from 'pocketbase'

// ─────────────────────────────────────────────────────────────────────────
// Real WebAuthn passkeys, verified by the api sidecar (served same-origin at
// /api/passkey/*). Enrolment binds a passkey to the signed-in
// recruiter; login is usernameless (discoverable credentials) and returns a
// minted PocketBase session for the store to adopt.
// ─────────────────────────────────────────────────────────────────────────

const BASE = '/api/passkey'

export function passkeySupported(): boolean {
  return typeof window !== 'undefined' && typeof window.PublicKeyCredential !== 'undefined'
}

async function postJson<T>(path: string, body: unknown, pbToken?: string): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(pbToken ? { 'X-PB-Token': pbToken } : {}),
    },
    body: JSON.stringify(body ?? {}),
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error((data as { error?: string })?.error || 'Passkey request failed.')
  return data as T
}

/** Enrol a new passkey for the currently signed-in recruiter (token required). */
export async function enrollPasskey(pbToken: string): Promise<void> {
  const options = await postJson<Parameters<typeof startRegistration>[0]['optionsJSON']>(
    '/register/begin',
    {},
    pbToken,
  )
  const response = await startRegistration({ optionsJSON: options })
  await postJson('/register/finish', { response }, pbToken)
}

/** Usernameless passkey sign-in → a minted PocketBase session. */
export async function loginWithPasskey(): Promise<{ token: string; record: RecordModel }> {
  const { flowId, options } = await postJson<{
    flowId: string
    options: Parameters<typeof startAuthentication>[0]['optionsJSON']
  }>('/login/begin', {})
  const response = await startAuthentication({ optionsJSON: options })
  return postJson<{ token: string; record: RecordModel }>('/login/finish', { flowId, response })
}

/** Turn raw WebAuthn / network errors into a short, friendly message. */
export function humanizePasskeyError(err: unknown): string {
  const e = err as { name?: string; message?: string }
  if (e?.name === 'NotAllowedError' || /timed out|cancel/i.test(e?.message || '')) {
    return 'Passkey prompt was dismissed — tap to try again.'
  }
  if (/no.*passkey|unrecognized|not found/i.test(e?.message || '')) {
    return 'No passkey found for this site on this device.'
  }
  return e?.message || 'Something went wrong with the passkey.'
}
